import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, CreditCard, Gift, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead, money } from "@/components/shop/common";

const STATUS_TEXT = { trialing: "Free trial", active: "Active", past_due: "Payment overdue", canceled: "Canceled", unpaid: "Unpaid", incomplete: "Incomplete" };
const date = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const PlanCard = ({ p, current, onPick, busy, canBuy, trialDays, trialUsed, discount }) => (
  <div className={`bg-card border rounded-sm p-6 flex flex-col gap-4 ${current ? "border-primary" : "border-border"}`} data-testid={`plan-card-${p.key}`}>
    <div>
      <p className="font-display text-3xl uppercase">{p.name}</p>
      <p className="mt-2"><span className="font-display text-5xl font-bold" data-testid={`plan-price-${p.key}`}>{money(p.amount)}</span><span className="text-muted-foreground"> /month</span></p>
      {discount && <p className="text-sm text-emerald-400 mt-1" data-testid={`plan-discount-${p.key}`}>{discount.percent_off}% off applied at checkout</p>}
    </div>
    <p className="text-sm text-muted-foreground">{p.description}</p>
    <ul className="text-sm space-y-1.5">
      {["AI web parts search", "AI work orders & shop assistant", "Estimates, invoices, inventory", p.staff_limit ? `Up to ${p.staff_limit} staff logins` : "Unlimited staff logins"].map((f) => (
        <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-primary shrink-0" /> {f}</li>
      ))}
    </ul>
    {canBuy && (
      <Button onClick={() => onPick(p.key)} disabled={!!busy} className="mt-auto h-11 rounded-sm font-semibold hover:bg-orange-600 hover:text-white" data-testid={`plan-select-${p.key}`}>
        {busy === p.key ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
        {trialUsed ? "Subscribe" : `Start ${trialDays}-day free trial`}
      </Button>
    )}
  </div>
);

export default function Billing() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/billing").then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { if (params.get("canceled")) toast.info("Checkout canceled — no charge was made"); }, [params]);

  const pick = async (plan_key) => {
    setBusy(plan_key);
    try { const { data } = await api.post("/billing/checkout", { plan_key, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  const portal = async () => {
    setBusy("portal");
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  if (!b) return <p className="text-muted-foreground">Loading…</p>;
  if (user?.role === "admin") return (
    <><PageHead eyebrow="Account" title="Billing" />
      <p className="text-muted-foreground" data-testid="billing-admin-note">You're the platform admin — no billing required. Manage shops' trials, discounts and plan prices in the Admin panel.</p></>
  );
  const s = b.subscription;
  const subscribed = s.has_subscription && ["trialing", "active", "past_due"].includes(s.status);
  return (
    <>
      <PageHead eyebrow="Account" title="Billing" />
      {!s.has_access && user?.role !== "admin" && (
        <div className="mb-6 flex items-center gap-3 border border-primary/50 bg-primary/10 rounded-sm px-4 py-3 text-sm" data-testid="billing-locked-banner">
          <Lock className="h-4 w-4 text-primary" />
          {b.is_owner ? `Pick a plan to unlock the shop. ${s.trial_used ? "" : `Your first ${b.trial_days} days are free — you won't be charged until the trial ends, and you can cancel anytime.`}`
            : `Your shop's subscription isn't active. Ask the shop owner (${user?.shop_owner_email}) to renew it.`}
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="billing-status">
        <div className="bg-card border border-border rounded-sm p-5"><p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</p>
          <p className="font-display text-3xl mt-1" data-testid="billing-status-text">{s.free_access ? "Free access" : STATUS_TEXT[s.status] || "No plan"}</p></div>
        <div className="bg-card border border-border rounded-sm p-5"><p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Plan</p>
          <p className="font-display text-3xl mt-1 capitalize" data-testid="billing-plan">{b.plans.find((p) => p.key === s.plan_key)?.name || "—"}</p></div>
        <div className="bg-card border border-border rounded-sm p-5"><p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {s.free_access ? "Free until" : s.status === "trialing" ? "Trial ends" : s.cancel_at_period_end ? "Ends on" : "Renews"}</p>
          <p className="font-display text-3xl mt-1" data-testid="billing-date">{date(s.free_access ? s.free_until : s.status === "trialing" ? s.trial_end : s.current_period_end)}</p></div>
      </div>
      {s.discount && b.is_owner && <p className="mb-6 flex items-center gap-2 text-sm text-emerald-400" data-testid="billing-discount"><Gift className="h-4 w-4" /> {s.discount.percent_off}% discount {s.discount.duration === "forever" ? "for as long as you subscribe" : s.discount.duration === "once" ? "on your first payment" : `for ${s.discount.months} months`}</p>}
      {subscribed && b.is_owner && (
        <Button onClick={portal} disabled={!!busy} variant="outline" className="mb-8 rounded-sm hover:text-white" data-testid="billing-portal-btn">
          {busy === "portal" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />} Manage billing, card or cancel
        </Button>
      )}
      {!b.is_owner && s.has_access && <p className="mb-6 text-sm text-muted-foreground" data-testid="billing-staff-note">Billing is managed by the shop owner ({user?.shop_owner_email}).</p>}
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
        {b.plans.map((p) => (
          <PlanCard key={p.key} p={p} current={s.plan_key === p.key && subscribed} onPick={pick} busy={busy} canBuy={b.is_owner && !subscribed}
            trialDays={b.trial_days} trialUsed={s.trial_used} discount={b.is_owner ? s.discount : null} />
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-6 font-mono">Test mode: use card 4242 4242 4242 4242, any future date, any CVC.</p>
    </>
  );
}
